// ── NHS API Response Types ──
import { getServiceByPostCode } from "../../dispatchers/DOSDispatcher.js";
interface NHSSession {
  start: { hours: string; minutes: string };
  end: { hours: string; minutes: string };
}

interface NHSDaySchedule {
  day: string;
  sessions: NHSSession[];
}

interface NHSSpecifiedDate {
  date: string; // "DD-MM-YYYY"
  sessions: NHSSession[];
}

interface NHSOpeningTimes {
  allHours: boolean;
  days: NHSDaySchedule[];
  specifiedDates: NHSSpecifiedDate[];
}

interface NHSCapacityStatus {
  rag: "GREEN" | "AMBER" | "RED";
  human: "HIGH" | "LOW" | "NONE";
  hex: string;
}

interface NHSServiceResponse {
  id: string;
  name: string;
  type: { id: string; name: string };
  odsCode: string;
  address: string[];
  postcode: string;
  phone: { public: string; nonPublic: string; fax: string };
  web: string;
  openingTimes: NHSOpeningTimes;
  referralInstructions: { callHandler: string; other: string };
  capacity: { status: NHSCapacityStatus };
  endpoints: any[];
  patientDistance: string;
  publicName: string;
  professionalReferralInformation: string | null;
}

// ── Display Model ──

export interface PharmacySearchResult {
  id: string;
  name: string; // publicName
  odsCode: string;
  serviceType: string;
  address: string;
  postcode: string;
  distance: number;
  telephone: string; // public phone
  telephoneNonPublic: string; // non-public phone (clinicians only)
  web: string;
  openingHours: {
    monday: string[];
    tuesday: string[];
    wednesday: string[];
    thursday: string[];
    friday: string[];
    saturday: string[];
    sunday: string[];
    bankholiday?: string[];
  };
  allHours: boolean;
  specificDates?: {
    date: string;
    hours: string[];
  }[];
  capacity: NHSCapacityStatus;
  referralCallHandler: string; // public-facing referral info
  referralOther: string; // clinician-only referral info
  professionalReferralInformation: string | null;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

// ── Helpers ──

/**
 * Check if a pharmacy is a DSP (online/distance selling) service
 * DSP services have ODS codes ending with 'DSP'
 */
export function isDSPService(odsCode: string): boolean {
  return odsCode.toUpperCase().endsWith("DSP");
}

function formatSession(session: NHSSession): string {
  return `${session.start.hours}:${session.start.minutes} - ${session.end.hours}:${session.end.minutes}`;
}

function sessionsToStrings(sessions: NHSSession[]): string[] {
  if (sessions.length === 0) return ["Closed"];
  return sessions.map(formatSession);
}

const DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

/**
 * Transform an NHS API service response into our display model
 */
export function transformNHSService(
  service: NHSServiceResponse,
): PharmacySearchResult {
  const openingHours: PharmacySearchResult["openingHours"] = {
    monday: ["Closed"],
    tuesday: ["Closed"],
    wednesday: ["Closed"],
    thursday: ["Closed"],
    friday: ["Closed"],
    saturday: ["Closed"],
    sunday: ["Closed"],
  };

  // Map standard day schedules
  for (const daySchedule of service.openingTimes.days) {
    const key = daySchedule.day.toLowerCase();
    if (key === "bankholiday") {
      openingHours.bankholiday = sessionsToStrings(daySchedule.sessions);
    } else if (DAY_KEYS.includes(key as any)) {
      (openingHours as any)[key] = sessionsToStrings(daySchedule.sessions);
    }
  }

  // Map specified dates
  const specificDates = service.openingTimes.specifiedDates.map((sd) => ({
    date: sd.date,
    hours: sessionsToStrings(sd.sessions),
  }));

  return {
    id: service.id,
    name: service.publicName || service.name,
    odsCode: service.odsCode,
    serviceType: service.type.name,
    address: service.address.join(", "),
    postcode: service.postcode,
    distance: parseFloat(service.patientDistance) || 0,
    telephone: service.phone.public,
    telephoneNonPublic: service.phone.nonPublic,
    web: service.web,
    openingHours,
    allHours: service.openingTimes.allHours,
    specificDates: specificDates.length > 0 ? specificDates : undefined,
    capacity: service.capacity.status,
    referralCallHandler: service.referralInstructions.callHandler,
    referralOther: service.referralInstructions.other,
    professionalReferralInformation: service.professionalReferralInformation,
  };
}

// ── Mock NHS API Data ──

const mockNHSServices: NHSServiceResponse[] = [
  {
    id: "2000168550",
    name: "Pharm+: Boots Pharmacy Bristol",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FLM49",
    address: ["45 Broadmead", "Bristol"],
    postcode: "BS1 3EA",
    phone: { public: "0117 929 4444", nonPublic: "0117 929 4445", fax: "" },
    web: "www.boots.com",
    openingTimes: {
      allHours: false,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "17", minutes: "00" },
            },
          ],
        },
        {
          day: "Sunday",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "16", minutes: "00" },
            },
          ],
        },
        {
          day: "Bankholiday",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "14", minutes: "00" },
            },
          ],
        },
      ],
      specifiedDates: [
        {
          date: "25-12-2025",
          sessions: [],
        },
        {
          date: "26-12-2025",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "14", minutes: "00" },
            },
          ],
        },
      ],
    },
    referralInstructions: {
      callHandler: "Walk-in service available. No appointment needed.",
      other:
        "Refer via PharmOutcomes. Contact pharmacy directly for urgent supply queries.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "0.3",
    publicName: "Boots Pharmacy - Broadmead",
    professionalReferralInformation: null,
  },
  {
    id: "2000168551",
    name: "Pharm+: Lloyds Pharmacy Gloucester Road",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FJR22",
    address: ["123 Gloucester Road", "Bristol"],
    postcode: "BS7 8AT",
    phone: { public: "0117 942 5566", nonPublic: "0117 942 5567", fax: "" },
    web: "www.lloydspharmacy.com",
    openingTimes: {
      allHours: false,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "08", minutes: "30" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "30" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "08", minutes: "30" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "30" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "08", minutes: "30" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "30" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "08", minutes: "30" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "30" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "08", minutes: "30" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "30" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "17", minutes: "00" },
            },
          ],
        },
        { day: "Sunday", sessions: [] },
        { day: "Bankholiday", sessions: [] },
      ],
      specifiedDates: [
        {
          date: "14-04-2026",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "14", minutes: "00" },
            },
          ],
        },
      ],
    },
    referralInstructions: {
      callHandler: "Appointments available. Call ahead to book.",
      other:
        "Use NHS referral pathway. ODS code required for electronic referrals.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "0.7",
    publicName: "Lloyds Pharmacy - Gloucester Road",
    professionalReferralInformation: null,
  },
  {
    id: "2000168552",
    name: "Pharm+: Well Pharmacy Whiteladies",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FKA91",
    address: ["78 Whiteladies Road", "Bristol"],
    postcode: "BS8 2QA",
    phone: { public: "0117 973 7788", nonPublic: "", fax: "" },
    web: "www.well.co.uk",
    openingTimes: {
      allHours: false,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
            {
              start: { hours: "14", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
          ],
        },
        { day: "Sunday", sessions: [] },
        { day: "Bankholiday", sessions: [] },
      ],
      specifiedDates: [],
    },
    referralInstructions: {
      callHandler: "Walk-in and telephone consultations available.",
      other: "",
    },
    capacity: { status: { rag: "AMBER", human: "LOW", hex: "#FFA500" } },
    endpoints: [],
    patientDistance: "1.2",
    publicName: "Well Pharmacy - Whiteladies Road",
    professionalReferralInformation: null,
  },
  {
    id: "2000168553",
    name: "Pharm+: Bedminster Pharmacy",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FVN88",
    address: ["234 North Street", "Bristol"],
    postcode: "BS3 1JD",
    phone: { public: "0117 966 3322", nonPublic: "0117 966 3323", fax: "" },
    web: "",
    openingTimes: {
      allHours: false,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "19", minutes: "00" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "19", minutes: "00" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "19", minutes: "00" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "19", minutes: "00" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "19", minutes: "00" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Sunday",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "14", minutes: "00" },
            },
          ],
        },
        { day: "Bankholiday", sessions: [] },
      ],
      specifiedDates: [
        { date: "25-12-2025", sessions: [] },
        { date: "26-12-2025", sessions: [] },
        { date: "01-01-2026", sessions: [] },
        {
          date: "04-05-2026",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "14", minutes: "00" },
            },
          ],
        },
      ],
    },
    referralInstructions: {
      callHandler: "Walk-in service. No booking required.",
      other: "Urgent supply referrals accepted via PharmOutcomes or telephone.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "1.5",
    publicName: "Bedminster Pharmacy",
    professionalReferralInformation: null,
  },
  {
    id: "2000168554",
    name: "Pharm+: Superdrug Pharmacy Cabot Circus",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FRK17",
    address: ["89 Cabot Circus", "Bristol"],
    postcode: "BS1 3BX",
    phone: { public: "0117 925 1122", nonPublic: "", fax: "" },
    web: "www.superdrug.com",
    openingTimes: {
      allHours: true,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Sunday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Bankholiday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
      ],
      specifiedDates: [
        {
          date: "25-12-2025",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "16", minutes: "00" },
            },
          ],
        },
      ],
    },
    referralInstructions: {
      callHandler: "Open 24 hours. Walk-in anytime.",
      other: "24hr service. Use PharmOutcomes for referrals.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "2.1",
    publicName: "Superdrug Pharmacy - Cabot Circus (24hr)",
    professionalReferralInformation: null,
  },
  {
    id: "2000168555",
    name: "Pharm+: Southville Pharmacy",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FGH55",
    address: ["145 North Street", "Bristol"],
    postcode: "BS3 1ER",
    phone: { public: "0117 963 2244", nonPublic: "", fax: "" },
    web: "",
    openingTimes: {
      allHours: false,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
          ],
        },
        { day: "Sunday", sessions: [] },
        { day: "Bankholiday", sessions: [] },
      ],
      specifiedDates: [],
    },
    referralInstructions: {
      callHandler: "",
      other: "",
    },
    capacity: { status: { rag: "RED", human: "NONE", hex: "#FF0000" } },
    endpoints: [],
    patientDistance: "2.4",
    publicName: "Southville Pharmacy",
    professionalReferralInformation: null,
  },
  {
    id: "2000168556",
    name: "Pharm+: Kingswood Pharmacy",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FPQ63",
    address: ["67 Regent Street", "Bristol"],
    postcode: "BS15 8JT",
    phone: { public: "0117 967 5544", nonPublic: "0117 967 5545", fax: "" },
    web: "",
    openingTimes: {
      allHours: false,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "13", minutes: "00" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "18", minutes: "00" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "17", minutes: "00" },
            },
          ],
        },
        { day: "Sunday", sessions: [] },
        {
          day: "Bankholiday",
          sessions: [
            {
              start: { hours: "10", minutes: "00" },
              end: { hours: "14", minutes: "00" },
            },
          ],
        },
      ],
      specifiedDates: [
        {
          date: "16-04-2026",
          sessions: [
            {
              start: { hours: "09", minutes: "00" },
              end: { hours: "11", minutes: "00" },
            },
          ],
        },
        { date: "17-04-2026", sessions: [] },
      ],
    },
    referralInstructions: {
      callHandler: "Walk-in available during opening hours.",
      other:
        "Contact pharmacist directly for clinical queries on non-public line.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "2.9",
    publicName: "Kingswood Pharmacy",
    professionalReferralInformation:
      "Use NHS Service Finder referral. Ensure patient consent is documented.",
  },
  {
    id: "2000168557",
    name: "Pharm+: Pharmacy2U Online Pharmacy",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FXP12DSP",
    address: ["Unit 4, Victoria Business Park", "Leeds"],
    postcode: "LS11 5QP",
    phone: { public: "0113 265 0222", nonPublic: "", fax: "" },
    web: "www.pharmacy2u.co.uk",
    openingTimes: {
      allHours: true,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Sunday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Bankholiday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
      ],
      specifiedDates: [],
    },
    referralInstructions: {
      callHandler: "Online pharmacy — orders placed via website or app.",
      other: "DSP service. No walk-in facility. Medicines dispatched by post.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "0.0",
    publicName: "Pharmacy2U (Online)",
    professionalReferralInformation: null,
  },
  {
    id: "2000168558",
    name: "Pharm+: Echo by LloydsPharmacy Online",
    type: { id: "131", name: "Pharmacy Urgent Medicines Supply" },
    odsCode: "FMR77DSP",
    address: ["1 Sapphire Court", "London"],
    postcode: "E1W 1AT",
    phone: { public: "020 7043 0000", nonPublic: "", fax: "" },
    web: "www.echo.co.uk",
    openingTimes: {
      allHours: true,
      days: [
        {
          day: "Monday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Tuesday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Wednesday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Thursday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Friday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Saturday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Sunday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
        {
          day: "Bankholiday",
          sessions: [
            {
              start: { hours: "00", minutes: "00" },
              end: { hours: "23", minutes: "59" },
            },
          ],
        },
      ],
      specifiedDates: [],
    },
    referralInstructions: {
      callHandler: "Online pharmacy — prescriptions managed via app.",
      other: "DSP service. No physical premises for walk-in. Delivery only.",
    },
    capacity: { status: { rag: "GREEN", human: "HIGH", hex: "#008000" } },
    endpoints: [],
    patientDistance: "0.0",
    publicName: "Echo by LloydsPharmacy (Online)",
    professionalReferralInformation: null,
  },
];

// Pre-transform mock data
const mockPharmacies: PharmacySearchResult[] =
  mockNHSServices.map(transformNHSService);

/**
 * Calculate distance between two postcodes (mock implementation)
 */
function calculateMockDistance(searchPostcode: string): number {
  return Math.round((Math.random() * 2.8 + 0.2) * 10) / 10;
}

/**
 * Search for pharmacies by postcode
 */
const getDayOpening = (service: any, dayName: string): string[] => {
  if (!service?.openingTimes) return ["Not available"];

  if (service.openingTimes.allHours) return ["Open 24 hours"];

  const day = service.openingTimes.days?.find((d: any) => d.day === dayName);

  if (!day || !day.sessions?.length) return ["Closed"];

  return day.sessions.map(
    (s: any) =>
      `${s.start.hours}:${s.start.minutes} - ${s.end.hours}:${s.end.minutes}`,
  );
};
export async function searchPharmaciesByPostcode(
  postcode: string,
  limit: number = 10,
  gender: string,
  // showServiceTypeFilter: boolean,
  onError?: (message: string) => void,
  serviceTypeId?: string,
): Promise<PharmacySearchResult[]> {
  const payload: any = {
    postCode: postcode,
    gender: gender,
    searchDistance: "0",
    serviceTypeIds: serviceTypeId,
  };

  const servicesResponse = await getServiceByPostCode(payload);
  if (servicesResponse?.error) {
    onError?.(servicesResponse.error.message ?? "Failed to fetch pharmacies");
    return [];
  }

  const services = servicesResponse?.success?.services ?? [];
  if (services.length === 0) {
    onError?.("No pharmacies found near this postcode");
    return [];
  }
  const results: PharmacySearchResult[] = services
    .map((s: any) => ({
      id: s?.id ?? "",
      name: s?.publicName ?? s?.name ?? "",
      odsCode: s?.odsCode || "",
      serviceType: s?.type.name || "",
      address: Array.isArray(s?.address) ? s.address.join(", ") : "",
      postcode: s?.postcode ?? "",
      distance: s?.patientDistance || 0,
      telephone: s?.phone?.public ?? "",
      telephoneNonPublic: s?.phone?.nonPublic ?? "",
      web: s?.web ?? "",
      openingHours: {
        monday: getDayOpening(s, "Monday"),
        tuesday: getDayOpening(s, "Tuesday"),
        wednesday: getDayOpening(s, "Wednesday"),
        thursday: getDayOpening(s, "Thursday"),
        friday: getDayOpening(s, "Friday"),
        saturday: getDayOpening(s, "Saturday"),
        sunday: getDayOpening(s, "Sunday"),
        bankholiday: getDayOpening(s, "Bankholiday"),
      },
      allHours: s?.openingTimes?.allHours || false,

      specificDates:
        s?.openingTimes?.specifiedDates?.map((d: any) => ({
          date: d.date,
          hours:
            d.sessions?.map(
              (session: any) =>
                `${session.start.hours}:${session.start.minutes} - ${session.end.hours}:${session.end.minutes}`,
            ) || [],
        })) || [],

      capacity: {
        rag: s?.capacity?.status?.rag,
        human: s?.capacity?.status?.human,
        hex: s?.capacity?.status?.hex,
      },
      referralCallHandler: s?.referralInstructions?.callHandler || "",
      referralOther: s?.referralInstructions?.other || "",
      professionalReferralInformation: s?.professionalReferralInformation || "",
      coordinates:
        s?.easting && s?.northing
          ? {
              lat: Number(s.northing),
              lng: Number(s.easting),
            }
          : undefined,
    }))
    .sort((a, b) => a.distance - b.distance)
    .slice(0, limit);
  return results;
}

/**
 * Get detailed information about a specific pharmacy
 */
export async function getPharmacyDetails(
  pharmacyId: string,
): Promise<PharmacySearchResult | null> {
  await new Promise((resolve) => setTimeout(resolve, 100));
  const pharmacy = mockPharmacies.find((p) => p.id === pharmacyId);
  return pharmacy || null;
}

/**
 * Validate UK postcode format (basic validation)
 */
export function isValidPostcode(postcode: string): boolean {
  const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
  return postcodeRegex.test(postcode.trim());
}
